import { useParams, useNavigate } from 'react-router-dom';
import { getDemoCompany } from '@/data/demoCompanyBranding';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Shield, ArrowRight, ChevronLeft } from 'lucide-react';

export default function DemoLogin() {
  const { companySlug } = useParams<{ companySlug: string }>();
  const navigate = useNavigate();
  const company = getDemoCompany(companySlug || '');

  if (!company) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Card className="p-8 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Demo Not Found</h1>
          <p className="text-gray-600 mb-6">The requested company demo does not exist.</p>
          <Button onClick={() => navigate('/')}>Return Home</Button>
        </Card>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{
        '--demo-primary': company.primaryColor,
        '--demo-primary-hex': company.primaryColorHex,
      } as React.CSSProperties}
    >
      {/* Demo Banner */}
      <div className="bg-gray-900 text-white py-2 px-4 text-center text-sm">
        <span className="opacity-75">This is a demo preview of the</span>
        <span className="font-semibold mx-1">{company.name}</span>
        <span className="opacity-75">white-label experience</span>
        <Button 
          variant="link" 
          className="text-white underline ml-2 p-0 h-auto"
          onClick={() => navigate('/')}
        >
          Exit Demo
        </Button>
      </div>

      {/* Main Login Area */}
      <div 
        className="flex-1 flex items-center justify-center p-6"
        style={{ backgroundColor: `hsl(${company.primaryColor})` }}
      >
        <div className="w-full max-w-md">
          {/* Logo & Welcome */}
          <div className="text-center mb-8">
            <div className="bg-white rounded-2xl p-6 shadow-xl inline-block mb-6">
              <img 
                src={company.logoUrl} 
                alt={`${company.name} logo`}
                className="h-12 md:h-16 w-auto object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'https://placehold.co/200x60?text=' + encodeURIComponent(company.name);
                }}
              />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
              {company.loginMessage}
            </h1>
            <p className="text-white/80 text-lg">{company.tagline}</p>
          </div>

          {/* Login Card */}
          <Card className="p-8 shadow-2xl">
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input 
                  id="email" 
                  type="email" 
                  placeholder="you@company.com"
                  className="h-12"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <PasswordInput 
                  id="password" 
                  placeholder="••••••••"
                  className="h-12"
                />
              </div>

              <Button 
                className="w-full h-12 text-base font-semibold"
                style={{ 
                  backgroundColor: `hsl(${company.primaryColor})`,
                }}
                onClick={() => navigate(`/demo/${companySlug}/dashboard`)}
              >
                Sign In
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-muted-foreground">Or continue with</span>
                </div>
              </div>

              <Button 
                variant="outline" 
                className="w-full h-12"
                onClick={() => navigate(`/demo/${companySlug}/dashboard`)}
              >
                <Shield className="mr-2 h-5 w-5" />
                Sign in with {company.name} SSO
              </Button>
            </div>

            <div className="mt-6 text-center">
              <a 
                href={`mailto:${company.supportEmail}`}
                className="text-sm text-muted-foreground hover:underline"
              >
                Need help? Contact {company.supportEmail}
              </a>
            </div>
          </Card>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-white/60 text-sm">
              Powered by MEDD Sim • Enterprise Training Platform
            </p>
          </div>
        </div>
      </div>

      {/* Back to Demos Link */}
      <div className="absolute top-16 left-4">
        <Button 
          variant="ghost" 
          className="text-white hover:bg-white/20"
          onClick={() => navigate('/demo')}
        >
          <ChevronLeft className="mr-1 h-4 w-4" />
          All Demos
        </Button>
      </div>
    </div>
  );
}
